import { createSlice } from '@reduxjs/toolkit';
import initialState from './initialState';
import {
  searchNetworks,
  createNetwork,
  fetchNetwork,
  updateNetwork,
  getNetworksByStatus,
  rejectInvitation,
  acceptInvitation,
  deleteInvitation,
  searchAgents,
  getNetworkSummary,
  requestNetworksUpgrade,
  getNetworksUpgradePending,
} from './services';
import {
  formatDateToDDMMYY,
  formatTimeToHHMM,
} from '@/components/shared/DateTransform';

function formatNetworks(networks, network_type, company_id) {
  const noTokkoBrokerNetworks = networks.filter((network) => network.id !== 3);
  return noTokkoBrokerNetworks.map((network) => {
    const myInvitation = network.invitations.find(
      (invitation) => invitation.company_id === company_id
    );
    const isFakeDate = new Date(network.created_at).getFullYear() < 2012;

    const safeFormatDate = (date) =>
      isFakeDate ? null : formatDateToDDMMYY(date);
    const safeFormatTime = (time) =>
      isFakeDate ? null : formatTimeToHHMM(time);

    return {
      type: network.is_association ? 'association' : 'collaboration',
      card_type: network_type,
      id: network.id,
      name: network.name,
      networkOwner: network.owner_name,
      description: network.description,
      commission: network.network_commission,
      logo: network.logo,
      createdBy: network.created_by_name,
      hasChat: network.has_group_chat,
      useFakeAddress: network.use_fake_address,
      shareInWeb: network.share_in_web,
      createdDate: safeFormatDate(network.created_at),
      createdTime: safeFormatTime(network.created_at),
      invitations: network.invitations,
      propertiesCount: network.properties_count,
      myInvitationId: myInvitation?.id,
      invitedBy: myInvitation?.invited_by_name,
      acceptedBy: myInvitation?.accepted_by_name,
      invitationDate: safeFormatDate(myInvitation?.created_at),
      invitationTime: safeFormatTime(myInvitation?.created_at),
      updatedDate: safeFormatDate(myInvitation?.updated_at),
      updatedTime: safeFormatTime(myInvitation?.updated_at),
    };
  });
}

export const NetworksSlice = createSlice({
  name: 'Networks',
  initialState,
  reducers: {
    setSearchQuery(state, action) {
      state.search.query = action.payload;
    },
    setSelectedParticipants(state, action) {
      state.selectedParticipants = action.payload.filter(
        (participant) => participant.status !== 'rejected'
      );
    },
    clearSelectedParticipants(state) {
      state.selectedParticipants = [];
    },
    setNetworkFormData(state, action) {
      state.network = {
        ...state.network,
        ...action.payload,
      };
    },
    resetNetwork(state) {
      state.network = { ...initialState.network };
    },
    resetCreationState(state) {
      state.creation = {
        isLoading: false,
        error: null,
      };
    },
    clearEditingNetwork(state) {
      state.editingNetwork = null;
      state.fetch = {
        isLoading: false,
        error: null,
      };
    },
  },
  extraReducers: (builder) => {
    if (searchNetworks) {
      builder
        .addCase(searchNetworks.pending, (state, action) => {
          const { search, page, pageSize } = action.meta?.arg || {};
          if (typeof search === 'string') {
            state.search.query = search;
          }
          if (page) state.search.page = page;
          if (pageSize) state.search.pageSize = pageSize;

          state.search.isLoading = true;
          state.search.error = null;
        })
        .addCase(searchNetworks.fulfilled, (state, action) => {
          const { results } = action.payload || {};
          state.search.results = results || [];
          state.search.isLoading = false;
          state.search.error = null;
        })
        .addCase(searchNetworks.rejected, (state, action) => {
          state.search.isLoading = false;
          state.search.error = action.error?.message || 'Search failed';
        });
    }
    builder
      .addCase(createNetwork.pending, (state) => {
        state.creation.isLoading = true;
        state.creation.error = null;
      })
      .addCase(createNetwork.fulfilled, (state) => {
        state.creation.isLoading = false;
        state.creation.error = null;
      })
      .addCase(createNetwork.rejected, (state, action) => {
        state.creation.isLoading = false;
        state.creation.error = action.error?.message || 'Creation failed';
      })
      .addCase(getNetworksByStatus.pending, (state) => {
        state.networks.loadingGetNetworks = true;
        state.networks.errorGetNetworks = false;
      })
      .addCase(getNetworksByStatus.fulfilled, (state, action) => {
        const { owned, pending, accepted, company_id } = action.payload;
        state.networks.ownNetworks = formatNetworks(owned, 'own', company_id);
        state.networks.pendingNetworks = formatNetworks(
          pending,
          'pending',
          company_id
        );
        state.networks.acceptedNetworks = formatNetworks(
          accepted,
          'accepted',
          company_id
        );

        state.networks.loadingGetNetworks = false;
        state.networks.errorGetNetworks = false;
      })
      .addCase(getNetworksByStatus.rejected, (state) => {
        state.networks.loadingGetNetworks = false;
        state.networks.errorGetNetworks = true;
      })
      .addCase(rejectInvitation.pending, (state, action) => {
        const { invitationId } = action.meta.arg;
        state.networks.invitationId = invitationId;
        state.networks.loadingRejectInvitation = true;
        state.networks.errorRejectInvitation = false;
      })
      .addCase(rejectInvitation.fulfilled, (state, action) => {
        const { networkId } = action.meta.arg;
        // Remove from "pending" list when invitation is rejected
        state.networks.pendingNetworks = state.networks.pendingNetworks.filter(
          (network) => network.id !== networkId
        );

        state.networks.loadingRejectInvitation = false;
        state.networks.errorRejectInvitation = false;
        state.networks.invitationId = null;
      })
      .addCase(rejectInvitation.rejected, (state) => {
        state.networks.loadingRejectInvitation = false;
        state.networks.errorRejectInvitation = true;
        state.networks.invitationId = null;
      })
      .addCase(acceptInvitation.pending, (state, action) => {
        const { invitationId } = action.meta.arg;
        state.networks.invitationId = invitationId;
        state.networks.loadingAcceptInvitation = true;
        state.networks.errorAcceptInvitation = false;
      })
      .addCase(acceptInvitation.fulfilled, (state, action) => {
        const { networkId } = action.meta.arg;
        const { _acceptedByFullName, updated_at } = action.payload;
        const acceptedNetwork = state.networks.pendingNetworks.find(
          (network) => network.id === networkId
        );
        state.networks.pendingNetworks = state.networks.pendingNetworks.filter(
          (network) => network.id !== networkId
        );
        if (acceptedNetwork) {
          state.networks.acceptedNetworks.push({
            ...acceptedNetwork,
            card_type: 'accepted',
            acceptedBy: _acceptedByFullName,
            updatedDate: formatDateToDDMMYY(updated_at),
            updatedTime: formatTimeToHHMM(updated_at),
          });
        }
        state.networks.loadingAcceptInvitation = false;
        state.networks.errorAcceptInvitation = false;
        state.networks.invitationId = null;
      })
      .addCase(acceptInvitation.rejected, (state) => {
        state.networks.loadingAcceptInvitation = false;
        state.networks.errorAcceptInvitation = true;
        state.networks.invitationId = null;
      })
      .addCase(deleteInvitation.pending, (state, action) => {
        const { invitationId } = action.meta.arg;
        state.networks.invitationId = invitationId;
        state.networks.loadingDeleteInvitation = true;
        state.networks.errorDeleteInvitation = false;
      })
      .addCase(deleteInvitation.fulfilled, (state, action) => {
        const { networkId } = action.payload;
        if (networkId) {
          // Remove from "accepted" list when leaving a network
          state.networks.acceptedNetworks =
            state.networks.acceptedNetworks.filter(
              (network) => network.id !== networkId
            );
        }
        state.networks.loadingDeleteInvitation = false;
        state.networks.errorDeleteInvitation = false;
        state.networks.invitationId = null;
      })
      .addCase(deleteInvitation.rejected, (state) => {
        state.networks.loadingDeleteInvitation = false;
        state.networks.errorDeleteInvitation = true;
        state.networks.invitationId = null;
      })
      .addCase(fetchNetwork.pending, (state) => {
        state.fetch.isLoading = true;
        state.fetch.error = null;
      })
      .addCase(fetchNetwork.fulfilled, (state, action) => {
        state.editingNetwork = action.payload;
        state.fetch.isLoading = false;
        state.fetch.error = null;
      })
      .addCase(fetchNetwork.rejected, (state, action) => {
        state.fetch.isLoading = false;
        state.fetch.error = action.error?.message || 'Failed to fetch network';
      })
      .addCase(updateNetwork.pending, (state) => {
        state.update.isLoading = true;
        state.update.error = null;
      })
      .addCase(updateNetwork.fulfilled, (state, action) => {
        state.editingNetwork = action.payload;
        state.update.isLoading = false;
        state.update.error = null;
      })
      .addCase(updateNetwork.rejected, (state, action) => {
        state.update.isLoading = false;
        state.update.error =
          action.error?.message || 'Failed to update network';
      })
      .addCase(searchAgents.pending, (state, action) => {
        const { q } = action.meta.arg || {};
        if (typeof q === 'string') {
          state.agentsSearch.query = q;
        }
        state.agentsSearch.isLoading = true;
        state.agentsSearch.error = null;
        state.agentsSearch.results = [];
      })
      .addCase(searchAgents.fulfilled, (state, action) => {
        const { users } = action.payload || {};
        state.agentsSearch.results = users || [];
        state.agentsSearch.isLoading = false;
        state.agentsSearch.error = null;
      })
      .addCase(searchAgents.rejected, (state, action) => {
        state.agentsSearch.isLoading = false;
        state.agentsSearch.error = action.error?.message || 'Search failed';
      })
      .addCase(getNetworkSummary.pending, (state) => {
        state.networks.loadingSummary = true;
        state.networks.errorSummary = false;
      })
      .addCase(getNetworkSummary.fulfilled, (state, action) => {
        const { plan_type, tier, networks, associations } = action.payload;
        state.networks.plan_type = plan_type;
        state.networks.tier = tier;
        state.networks.limits.networks = {
          current: networks.current,
          max: networks.max,
          available: networks.available,
          can_create: networks.can_create,
        };
        state.networks.limits.associations = {
          current: associations.current,
          max: associations.max,
          available: associations.available,
          can_create: associations.can_create,
          max_association_companies:
            associations.max_association_companies ?? null,
        };
        state.networks.loadingSummary = false;
        state.networks.errorSummary = false;
      })
      .addCase(getNetworkSummary.rejected, (state) => {
        state.networks.loadingSummary = false;
        state.networks.errorSummary = true;
      })
      .addCase(requestNetworksUpgrade.pending, (state) => {
        state.networks.loadingRequestUpgrade = true;
        state.networks.errorRequestUpgrade = false;
      })
      .addCase(requestNetworksUpgrade.fulfilled, (state) => {
        state.networks.upgradePending = true;
        state.networks.loadingRequestUpgrade = false;
        state.networks.errorRequestUpgrade = false;
      })
      .addCase(requestNetworksUpgrade.rejected, (state) => {
        state.networks.loadingRequestUpgrade = false;
        state.networks.errorRequestUpgrade = true;
      })
      .addCase(getNetworksUpgradePending.pending, (state) => {
        state.networks.loadingGetUpgradePending = true;
        state.networks.errorGetUpgradePending = false;
      })
      .addCase(getNetworksUpgradePending.fulfilled, (state, action) => {
        state.networks.upgradePending =
          action.payload?.private_networks || false;
        state.networks.loadingGetUpgradePending = false;
        state.networks.errorGetUpgradePending = false;
      })
      .addCase(getNetworksUpgradePending.rejected, (state) => {
        state.networks.loadingGetUpgradePending = false;
        state.networks.errorGetUpgradePending = true;
      });
  },
});

export const {
  setSearchQuery,
  setSelectedParticipants,
  clearSelectedParticipants,
  setNetworkFormData,
  resetNetwork,
  resetCreationState,
  clearEditingNetwork,
} = NetworksSlice.actions;

export const selectNetworksSearch = (state) => state.NetworksSlice.search;
export const selectAgentsSearch = (state) => state.NetworksSlice.agentsSearch;
export const selectNetworksSelectedParticipants = (state) =>
  state.NetworksSlice.selectedParticipants;
export const selectNetwork = (state) => state.NetworksSlice.network;
export const selectNetworkCreation = (state) => state.NetworksSlice.creation;
export const selectNetworks = (state) => state.NetworksSlice.networks;
export const selectEditingNetwork = (state) =>
  state.NetworksSlice.editingNetwork;
export const selectNetworkFetch = (state) => state.NetworksSlice.fetch;
export const selectNetworkUpdate = (state) => state.NetworksSlice.update;
export const selectNetworkTier = (state) =>
  state.NetworksSlice.networks.tier ?? initialState.networks.tier;
export const selectNetworkLimits = (state) =>
  state.NetworksSlice.networks.limits ?? initialState.networks.limits;
export const selectNetworkPlanType = (state) =>
  state.NetworksSlice.networks.plan_type ?? initialState.networks.plan_type;
export const selectNetworkSummaryLoading = (state) =>
  state.NetworksSlice.networks.loadingSummary ??
  initialState.networks.loadingSummary;
export const selectNetworkUpgradePending = (state) =>
  state.NetworksSlice.networks.upgradePending ??
  initialState.networks.upgradePending;
export const selectMaxAssociationCompanies = (state) =>
  state.NetworksSlice.networks.limits?.associations
    ?.max_association_companies ?? null;

export default NetworksSlice.reducer;
